import _get from 'lodash/get'
import * as z from 'zod'

import * as schemaUtils from '@flows/configs/shared/schemaUtils'
import type { StepConfigParams } from '@flows/configs/types'
import type { AnyStepConfiguration, FlowValues } from '@flows/core/types/stepTypes'

const schema = z.enum([
  'single-false',
  'partner-false',
  'spouse-false',
  'single-true',
  'partner-true',
  'spouse-true',
])

const legal_coverage_options = (params: StepConfigParams) =>
  ({
    schema: schemaUtils.generateZodObjectFromPaths({
      [params.helperFunctions.getHelperPath('coverage_options')]: schema,
    }),
    stepContent: [
      {
        type: 'title',
        text: {
          type: 'translatableText',
          translationKey: 'sharedFlows_legal:steps.coverage_options.title',
        },
      },
      {
        type: 'choice',
        shouldTrackValues: true,
        name: params.helperFunctions.getHelperPath('coverage_options'),
        options: [
          {
            label: {
              type: 'translatableText',
              translationKey: 'sharedFlows_legal:steps.coverage_options.me',
            },
            value: 'single-false',
          },
          {
            label: {
              type: 'translatableText',
              translationKey: 'sharedFlows_legal:steps.coverage_options.partner',
            },
            value: 'partner-false',
          },
          {
            label: {
              type: 'translatableText',
              translationKey: 'sharedFlows_legal:steps.coverage_options.spouse',
            },
            value: 'spouse-false',
          },
          {
            label: {
              type: 'translatableText',
              translationKey: 'sharedFlows_legal:steps.coverage_options.children',
            },
            value: 'single-true',
          },
          {
            label: {
              type: 'translatableText',
              translationKey: 'sharedFlows_legal:steps.coverage_options.children_with_partner',
            },
            value: 'partner-true',
          },
          {
            label: {
              type: 'translatableText',
              translationKey: 'sharedFlows_legal:steps.coverage_options.children_with_spouse',
            },
            value: 'spouse-true',
          },
        ],
      },
      {
        type: 'infoBox',
        text: {
          type: 'translatableText',
          translationKey: 'sharedFlows_legal:steps.coverage_options.info.text',
        },
      },
    ],
    subFlow: params.subFlow,
    onSubmit: {
      type: 'simpleHook',
      inputs: [params.helperFunctions.getHelperPath('coverage_options')],
      outputs: [
        params.helperFunctions.getParamPath('partner_coverage_type'),
        params.helperFunctions.getParamPath('covering_children'),
      ],
      callback: (formValues: FlowValues) => {
        const productHelperValues = formValues.helperValues
        const helperValues = _get(productHelperValues, params.productKey)

        if (!helperValues) {
          throw new Error('helperValues not found')
        }

        const coverageOptions = z
          .object({
            coverage_options: schema,
          })
          .parse(helperValues)
          .coverage_options.split('-')
        return {
          formValues: {
            [params.helperFunctions.getParamPath('partner_coverage_type')]: coverageOptions[0],
            [params.helperFunctions.getParamPath('covering_children')]:
              coverageOptions[1] === 'true', // config data variable is a boolean,
          },
        }
      },
    },
    nextStepConfig: {
      valuePath: params.helperFunctions.getParamPath('partner_coverage_type'),
      valueToStepName: {
        single: 'legal_quote',
        spouse: 'legal_partner_name',
        partner: 'legal_partner_name',
      },
    },
  }) as const satisfies AnyStepConfiguration

export default legal_coverage_options
